/**
 * The live weighted total shown while scoring.
 *
 * This duplicates the server's arithmetic deliberately: the editor must show
 * the total updating as each score changes, and a round trip per keystroke is
 * both slow and wrong to depend on. The SERVER's value is authoritative — this
 * is a preview, replaced by the response on save.
 *
 * Rounding matches `apps/api/src/modules/startups/scorecard.ts`: rounded BEFORE
 * banding, because floating-point multiplication can leave a total at
 * 3.7999999999999994 where the arithmetic says 3.8, and that would show band B
 * for a startup the server will band A.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.6, PRD §5.2
 */

/** Decimal places the total is rounded to, matching the server. */
const TOTAL_PRECISION = 3;

export interface Criterion {
  key: string;
  label: string;
  weight: number;
}

/** PRD §5.2 band boundaries. */
export const BAND_THRESHOLDS = { a: 3.8, b: 2.5 } as const;

export interface LiveTotal {
  /** Null until every criterion has a score. */
  total: number | null;
  band: 'A' | 'B' | 'C' | null;
  isComplete: boolean;
  missingCount: number;
}

/** Rounds half-up to a fixed number of decimals. */
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * The band a total falls into.
 *
 * Boundaries are INCLUSIVE at the lower edge: PRD §5.2 gives A as ≥3.8, so
 * exactly 3.8 is band A.
 */
export function bandFor(total: number): 'A' | 'B' | 'C' {
  if (total >= BAND_THRESHOLDS.a) return 'A';
  if (total >= BAND_THRESHOLDS.b) return 'B';
  return 'C';
}

/**
 * Computes the live total from the scores entered so far.
 *
 * A partial scorecard has NO total. Averaging what is present would report a
 * confident figure from an incomplete assessment — a startup scored 5 on one
 * criterion would show as band A before anyone had looked at the rest.
 */
export function liveTotal(
  criteria: readonly Criterion[],
  scores: Readonly<Record<string, number | undefined>>,
): LiveTotal {
  const missing = criteria.filter((criterion) => scores[criterion.key] === undefined);

  if (criteria.length === 0 || missing.length > 0) {
    return { total: null, band: null, isComplete: false, missingCount: missing.length };
  }

  /* Non-null asserted rather than defaulted: the guard above returned unless
     every criterion has a score, so a `?? 0` would be an unreachable branch
     that also silently scored a missing criterion as zero. */
  const raw = criteria.reduce(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- see above
    (sum, criterion) => sum + scores[criterion.key]! * criterion.weight,
    0,
  );

  const total = roundTo(raw, TOTAL_PRECISION);

  return { total, band: bandFor(total), isComplete: true, missingCount: 0 };
}
